import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Sparkles, RefreshCw, Database, Zap, TrendingUp, Users, Brain } from "lucide-react";

// ---------------------------------------------------------------------------
// Mini bar chart via CSS — sem dependências externas
// ---------------------------------------------------------------------------
const MiniBarChart = ({ data }) => {
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1.5 h-24 w-full" data-testid="ai-daily-chart">
      {data.map((d) => {
        const pct = Math.round((d.count / maxVal) * 100);
        const label = d.date.slice(5); // MM-DD
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
            <span className="text-[10px] font-mono text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
              {d.count}
            </span>
            <div className="w-full relative flex items-end" style={{ height: "72px" }}>
              <div
                className="w-full rounded-t bg-purple-500/60 hover:bg-purple-400/80 transition-all"
                style={{ height: `${Math.max(pct, 2)}%` }}
                title={`${d.date}: ${d.count} chamadas`}
              />
            </div>
            <span className="text-[9px] text-slate-500 font-mono">{label}</span>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
const StatCard = ({ icon: Icon, label, value, sub, color = "purple", testId }) => {
  const colors = {
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/20",
    cyan:   "text-cyan-400   bg-cyan-500/10   border-cyan-500/20",
    emerald:"text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    orange: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-5 h-5 opacity-80" />
      </div>
      <div className="text-2xl font-mono font-bold">{value ?? "—"}</div>
      <div className="text-xs font-medium mt-0.5">{label}</div>
      {sub && <div className="text-[11px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------
const AiStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/ai-stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Erro ao carregar estatísticas de IA.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500 gap-3">
        <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">A carregar estatísticas de IA...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
        {error}
      </div>
    );
  }

  const cachePct = stats.total_ai_calls + stats.total_cache_hits > 0
    ? Math.round((stats.total_cache_hits / (stats.total_ai_calls + stats.total_cache_hits)) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="ai-stats-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-400" />
          <h2 className="font-chivo text-lg text-cockpit-text-bright">Estatísticas de IA</h2>
          <span className="text-xs text-slate-500 font-mono">
            Limite diário: {stats.daily_limit_per_user}/aluno
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStats}
          className="btn-secondary-cockpit"
          data-testid="ai-stats-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" />
          Atualizar
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={Sparkles}
          label="Chamadas à IA"
          value={stats.total_ai_calls.toLocaleString("pt-PT")}
          sub="total acumulado"
          color="purple"
          testId="stat-total-calls"
        />
        <StatCard
          icon={Database}
          label="Hits de Cache"
          value={stats.total_cache_hits.toLocaleString("pt-PT")}
          sub={`${cachePct}% do total de pedidos`}
          color="cyan"
          testId="stat-cache-hits"
        />
        <StatCard
          icon={Zap}
          label="Créditos Poupados"
          value={stats.credits_saved.toLocaleString("pt-PT")}
          sub="respostas via cache"
          color="emerald"
          testId="stat-credits-saved"
        />
        <StatCard
          icon={TrendingUp}
          label="Questões em Cache"
          value={stats.cached_questions.toLocaleString("pt-PT")}
          sub={`hoje: ${stats.calls_today} chamadas`}
          color="orange"
          testId="stat-cached-questions"
        />
      </div>

      {/* Gráfico de utilização + top utilizadores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Gráfico diário */}
        <Card className="instrument-panel">
          <CardHeader className="pb-3">
            <CardTitle className="font-chivo text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              Chamadas IA — últimos 7 dias
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.daily_usage && stats.daily_usage.length > 0 ? (
              <MiniBarChart data={stats.daily_usage} />
            ) : (
              <p className="text-sm text-slate-500 text-center py-6">Sem dados.</p>
            )}
          </CardContent>
        </Card>

        {/* Top utilizadores */}
        <Card className="instrument-panel">
          <CardHeader className="pb-3">
            <CardTitle className="font-chivo text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              Top utilizadores
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_users && stats.top_users.length > 0 ? (
              <div className="space-y-2" data-testid="ai-top-users">
                {stats.top_users.map((u, i) => (
                  <div key={u.user_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-800 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-mono text-slate-600 w-4 shrink-0">{i + 1}</span>
                      <div className="min-w-0">
                        <p className="text-sm text-cockpit-text-bright truncate">{u.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      <span className="text-sm font-mono text-purple-300">{u.total_calls}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-6">Sem dados de utilizadores.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cache efficiency info */}
      <Card className="instrument-panel">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Eficiência do cache</span>
            <span className="text-xs font-mono text-purple-400">{cachePct}%</span>
          </div>
          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-cyan-500 rounded-full transition-all duration-700"
              style={{ width: `${cachePct}%` }}
              data-testid="cache-efficiency-bar"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            {stats.total_cache_hits} de {stats.total_ai_calls + stats.total_cache_hits} pedidos foram servidos pelo cache — {cachePct}% de chamadas à IA evitadas.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AiStatsPanel;
